import type {
  SettingsSectionDefinition,
  SettingsSectionRegistrations,
} from '../types/types'

export type SettingsSectionAccess = {
  areIncidentAudioAlertsEnabled: boolean
  isAiOperatorEnabled: boolean
  isCreateLlmIncidentsEnabled: boolean
  isDatabaseResetEnabled: boolean
  isDevelopmentModeEnabled: boolean
  isDeviceHealthSettingsEnabled: boolean
  isDeviceLabelingEnabled: boolean
  isShowDarkModeToggleEnabled: boolean
  isImplementer: boolean
  isQuickReportEnabled: boolean
  shouldShowAnnouncementSettings: boolean
  shouldShowCreateManualIncidentCategory: boolean
  shouldShowCustomerSecretProviderSettings: boolean
  shouldShowExternalAccountSettings: boolean
  shouldShowQuickReport: boolean
  shouldShowSettingsPage: boolean
}

export const getSettingsSections = (
  access: SettingsSectionAccess,
  registrations: SettingsSectionRegistrations = {}
): SettingsSectionDefinition[] => {
  const sections: SettingsSectionDefinition[] = [
    {
      descriptionKey: 'settings:rebrand_account_description',
      id: 'account',
      items: [
        { id: 'account-information', isVisible: true },
        {
          id: 'dark-mode',
          isVisible: access.isShowDarkModeToggleEnabled,
        },
        {
          id: 'incident-audio-alerts',
          isVisible: access.areIncidentAudioAlertsEnabled,
        },
        {
          // Flag-only gate: Account carries no role gate, so Database Reset is
          // reachable by every authenticated role (PM, Operator, Implementer,
          // etc.) whenever `salesDatabaseReset` is on — see ENG-33231.
          id: 'database-reset',
          isVisible: access.isDatabaseResetEnabled,
        },
      ],
      labelKey: 'settings:rebrand_account_title',
      titleKey: 'settings:rebrand_account_title',
    },
    {
      descriptionKey: 'settings:rebrand_general_description',
      id: 'general',
      items: [
        {
          id: 'organization-information',
          isVisible: access.shouldShowSettingsPage,
        },
        {
          id: 'announcements',
          isVisible: access.shouldShowAnnouncementSettings,
        },
        {
          id: 'application-information',
          isVisible: true,
        },
        {
          id: 'generate-llm-incidents',
          isVisible:
            access.isDevelopmentModeEnabled &&
            access.isCreateLlmIncidentsEnabled,
        },
      ],
      labelKey: 'settings:rebrand_general_title',
      titleKey: 'settings:rebrand_general_title',
    },
    {
      descriptionKey: 'settings:rebrand_users_description',
      id: 'users-access',
      items: [
        {
          id: 'users',
          isVisible: access.shouldShowSettingsPage,
        },
      ],
      labelKey: 'settings:rebrand_users_access_label',
      titleKey: 'settings:rebrand_users_title',
    },
    {
      descriptionKey: 'settings:rebrand_incidents_description',
      id: 'incidents',
      items: [
        {
          id: 'incident-definitions',
          isVisible: access.shouldShowCreateManualIncidentCategory,
        },
        {
          id: 'quick-report',
          isVisible:
            access.isQuickReportEnabled && access.shouldShowQuickReport,
        },
      ],
      labelKey: 'settings:rebrand_incidents_label',
      titleKey: 'settings:rebrand_incidents_title',
    },
    {
      descriptionKey: 'settings:rebrand_ai_operator_description',
      id: 'ai-operator',
      items: [
        {
          id: 'ai-operator',
          isVisible:
            access.shouldShowSettingsPage && access.isAiOperatorEnabled,
        },
      ],
      labelKey: 'settings:rebrand_ai_operator_title',
      titleKey: 'settings:rebrand_ai_operator_title',
    },
    {
      descriptionKey: 'settings:rebrand_devices_description',
      id: 'devices',
      items: [
        {
          id: 'device-health',
          isVisible:
            access.shouldShowSettingsPage &&
            access.isDeviceHealthSettingsEnabled,
        },
      ],
      labelKey: 'settings:rebrand_devices_title',
      titleKey: 'settings:rebrand_devices_title',
    },
    {
      descriptionKey: 'settings:rebrand_sources_description',
      id: 'sources',
      items: [
        {
          id: 'external-accounts',
          isVisible: access.shouldShowExternalAccountSettings,
        },
        {
          id: 'customer-secret-providers',
          isVisible: access.shouldShowCustomerSecretProviderSettings,
        },
      ],
      labelKey: 'settings:rebrand_sources_title',
      titleKey: 'settings:rebrand_sources_title',
    },
    {
      descriptionKey: 'settings:rebrand_labels_description',
      id: 'labels',
      items: [
        {
          id: 'labels',
          isVisible: access.isDeviceLabelingEnabled,
        },
      ],
      labelKey: 'settings:rebrand_labels_title',
      titleKey: 'settings:rebrand_labels_title',
    },
    {
      descriptionKey: 'settings:rebrand_feature_flags_description',
      id: 'feature-flags',
      items: [
        {
          id: 'feature-flags',
          isVisible: access.isImplementer,
        },
      ],
      labelKey: 'settings:rebrand_feature_flags_title',
      titleKey: 'settings:rebrand_feature_flags_title',
    },
  ]

  return sections.map((section) => {
    const registration = registrations[section.id]

    return {
      ...section,
      actions: registration?.actions,
      contentState: registration?.contentState,
      dirtyState: registration?.dirtyState,
      emptyState: registration?.emptyState,
      errorState: registration?.errorState,
      items: section.items.map((item) => ({
        ...item,
        content: registration?.itemContent?.[item.id],
      })),
      loadingState: registration?.loadingState,
      readOnlyState: registration?.readOnlyState,
    }
  })
}

export const getVisibleSettingsSections = (
  sections: SettingsSectionDefinition[]
) => sections.filter((section) => section.items.some((item) => item.isVisible))
